import { lazy, type ComponentType } from 'react';

const ReplacementPage = lazy(() =>
  import('@/features/replacement/ReplacementPage').then((module) => ({
    default: module.ReplacementPage,
  })),
);
const MyHandoffsPage = lazy(() =>
  import('@/field/MyHandoffsPage').then((module) => ({
    default: module.MyHandoffsPage,
  })),
);
const ProposalsPage = lazy(() =>
  import('@/features/proposals/ProposalsPage').then((module) => ({
    default: module.ProposalsPage,
  })),
);
const ProposalApprovalsPage = lazy(() =>
  import('@/features/proposals/ProposalApprovalsPage').then((module) => ({
    default: module.ProposalApprovalsPage,
  })),
);
export const PublicProposalPage = lazy(() =>
  import('@/features/proposals/PublicProposalPage').then((module) => ({
    default: module.PublicProposalPage,
  })),
);
const ConsultationTasksPage = lazy(() =>
  import('@/features/consultation/ConsultationSettingsPage').then((module) => ({
    default: module.ConsultationTasksPage,
  })),
);
const PipelinePage = lazy(() =>
  import('@/features/pipeline/PipelinePage').then((module) => ({ default: module.PipelinePage })),
);
const NeedsAttentionPage = lazy(() =>
  import('@/features/pipeline/PipelinePage').then((module) => ({
    default: module.NeedsAttentionPage,
  })),
);
const LeadsPage = lazy(() =>
  import('@/features/leads/LeadsPage').then((module) => ({ default: module.LeadsPage })),
);
const LeadDetailPage = lazy(() =>
  import('@/features/leads/LeadsPage').then((module) => ({ default: module.LeadDetailPage })),
);
export const PublicLeadPage = lazy(() =>
  import('@/features/leads/PublicLeadPage').then((module) => ({ default: module.PublicLeadPage })),
);
const DealHub = lazy(() =>
  import('@/features/pipeline/DealHub').then((module) => ({ default: module.DealHub })),
);
const JobsPage = lazy(() =>
  import('@/features/jobs/JobsPage').then((module) => ({ default: module.JobsPage })),
);
const DispatchPage = lazy(() =>
  import('@/features/dispatch/DispatchPage').then((module) => ({ default: module.DispatchPage })),
);
const HandoffPage = lazy(() =>
  import('@/features/handoff/HandoffPage').then((module) => ({ default: module.HandoffPage })),
);

const FollowUpPage = lazy(() =>
  import('@/features/followup/FollowUpPage').then((module) => ({ default: module.FollowUpPage })),
);
const ReportsPage = lazy(() =>
  import('@/features/reports/ReportsPage').then((module) => ({ default: module.ReportsPage })),
);

const TeamPage = lazy(() =>
  import('@/features/team/TeamPage').then((module) => ({ default: module.TeamPage })),
);
const TeamMemberPage = lazy(() =>
  import('@/features/team/TeamMemberPage').then((module) => ({ default: module.TeamMemberPage })),
);
const JobDetailPage = lazy(() =>
  import('@/features/jobs/JobDetailPage').then((module) => ({ default: module.JobDetailPage })),
);
const BookJobPage = lazy(() =>
  import('@/features/jobs/booking/BookJobPage').then((module) => ({
    default: module.BookJobPage,
  })),
);
const CustomerDetailPage = lazy(() =>
  import('@/features/customers/CustomerDetailPage').then((module) => ({
    default: module.CustomerDetailPage,
  })),
);
const CustomerDuplicatesPage = lazy(() =>
  import('@/features/customers/CustomerDuplicatesPage').then((module) => ({
    default: module.CustomerDuplicatesPage,
  })),
);
const CustomerImportPage = lazy(() =>
  import('@/features/customers/CustomerImportPage').then((module) => ({
    default: module.CustomerImportPage,
  })),
);

const CatalogItemDetailPage = lazy(() =>
  import('@/features/pricebook/CatalogItemDetailPage').then((module) => ({
    default: module.CatalogItemDetailPage,
  })),
);
const PricebookImportPage = lazy(() =>
  import('@/features/pricebook/PricebookImportPage').then((module) => ({
    default: module.PricebookImportPage,
  })),
);
const PricebookPage = lazy(() =>
  import('@/features/pricebook/PricebookPage').then((module) => ({
    default: module.PricebookPage,
  })),
);

/** The screens that exist; everything else in the navigation is a placeholder. */
const SettingsPage = lazy(() =>
  import('@/features/settings/SettingsPage').then((module) => ({ default: module.SettingsPage })),
);

export const CORE_PAGES: Record<string, ComponentType> = {
  settings: SettingsPage,
  proposals: ProposalsPage,
  replacement: ReplacementPage,
  'replacement-handoff': MyHandoffsPage,
  leads: LeadsPage,
  pipeline: PipelinePage,
  team: TeamPage,
  dispatch: DispatchPage,
  jobs: JobsPage,
  customers: CustomerDetailPage,
  pricebook: PricebookPage,
  handoff: HandoffPage,
  followup: FollowUpPage,
  reports: ReportsPage,
};

/** The item detail page, bound to the catalogue its route belongs to. */
function ServiceDetailPage(): React.JSX.Element {
  return <CatalogItemDetailPage itemType="SERVICE" />;
}

/** The item detail page, bound to the equipment catalogue. */
function EquipmentDetailPage(): React.JSX.Element {
  return <CatalogItemDetailPage itemType="EQUIPMENT" />;
}

/**
 * Screens that live beneath a module's own path.
 *
 * Keyed by navigation id and mounted inside that module's permission guard, so a
 * sub-route cannot be reached by someone who may not see the module it belongs
 * to. They are absent from the rail on purpose: a customer's detail page is not
 * a destination you navigate to, it is somewhere you arrive from a list.
 *
 * A child that writes needs more than the module's read permission; `permission`
 * names the capability its endpoints demand, so a reader is not shown a wizard
 * whose every request would 403.
 */
export const CORE_CHILD_ROUTES: Record<
  string,
  readonly {
    readonly path: string;
    readonly element: ComponentType;
    readonly permission?: string;
  }[]
> = {
  leads: [{ path: ':id', element: LeadDetailPage }],
  proposals: [
    { path: 'approvals', element: ProposalApprovalsPage, permission: 'PRICING_EXCEPTION_APPROVE' },
    { path: ':id', element: ProposalsPage },
  ],
  pipeline: [
    { path: 'needs-attention', element: NeedsAttentionPage },
    // The deal hub owns everything beneath a deal: its tabs, and the old consultation,
    // closing-call and lost-sale paths, which it redirects to the tab that now holds them.
    { path: ':id/*', element: DealHub },
  ],
  team: [{ path: ':id', element: TeamMemberPage }],
  jobs: [
    { path: 'consultation-tasks', element: ConsultationTasksPage },
    // Declared before ':id' so that /jobs/new is the booking dialog and not a job called "new".
    { path: 'new', element: BookJobPage, permission: 'JOB_WRITE' },
    { path: ':id', element: JobDetailPage },
  ],
  customers: [
    // Declared before ':id' so that /customers/import is the import wizard
    // rather than a customer whose identifier happens to be "import".
    { path: 'import', element: CustomerImportPage, permission: 'CUSTOMER_WRITE' },
    { path: 'duplicates', element: CustomerDuplicatesPage },
    { path: ':id', element: CustomerDetailPage },
  ],

  pricebook: [
    // Declared before the item routes so that /pricebook/import is the wizard
    // rather than a catalogue whose name happens to be "import".
    { path: 'import', element: PricebookImportPage, permission: 'PRICEBOOK_MANAGE' },
    { path: 'services/:id', element: ServiceDetailPage },
    { path: 'equipment/:id', element: EquipmentDetailPage },
  ],
};
